import { useCallback, useEffect, useState } from 'react'
import { faTimer } from '@fortawesome/pro-regular-svg-icons'

import {
  Button,
  Card,
  CardColors,
  Icon,
  Image,
  Pill,
  Text,
  type PillColor,
} from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { AnnualTax1040WrapUpModal } from '@/features/taxes/annual-taxes/components/AnnualTaxWrapUp'
import {
  fetchAnnualTaxOutcomesIfNeeded,
  selectAnnualTaxOutcomesByFormId,
} from '@/features/taxes/annual-taxes/annualTaxOutcome.slice'
import {
  FETCH_TAXFYLE_FORM_JOB_URL_KEY,
  fetchTaxFyleJobUrl,
} from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import { useOpenTaxfylePortal } from '@/features/taxes/annual-taxes/useOpenTaxfylePortal'
import {
  FormFilingJobSubstep,
  YearEndModuleStatusOptions,
} from '@/features/year-end-module-status/yearEndModuleStatus.slice'
// A pure label map, not a Semantic component: safe to share with the year end
// cards so the two cannot drift apart.
import { getDefaultButtonText } from '@/features/year-end-module-status/components/YearEndModuleStatusBaseCard'

import type { PostTaxYearForm } from '../taxfyle-short-form/taxfyleShortForm.slice'

const FORM_1120_S = 'form_1120_s'

/**
 * The year end filing cards cannot be reused here: they are built on Semantic
 * components whose styles only load under the old design system, and the
 * archive is a V2 route. The status vocabulary and copy follow them so a return
 * reads the same wherever the user finds it.
 */
interface StatusLabel {
  text: string
  color: PillColor
}

const BUTTON_STATUSES: YearEndModuleStatusOptions[] = [
  YearEndModuleStatusOptions.actionRequired,
  YearEndModuleStatusOptions.waitingOnHeard,
  YearEndModuleStatusOptions.cancelled,
]

const STATUS_LABELS: Partial<Record<YearEndModuleStatusOptions, StatusLabel>> =
  {
    [YearEndModuleStatusOptions.upcoming]: { text: 'Upcoming', color: 'gray' },
    [YearEndModuleStatusOptions.waitingOnHeard]: {
      text: 'Waiting on Heard',
      color: 'blue',
    },
    [YearEndModuleStatusOptions.inProgress]: {
      text: 'In Progress',
      color: 'orange',
    },
    [YearEndModuleStatusOptions.actionRequired]: {
      text: 'Action Required',
      color: 'yellow',
    },
    [YearEndModuleStatusOptions.complete]: { text: 'Complete', color: 'green' },
    [YearEndModuleStatusOptions.cancelled]: {
      text: 'Cancelled',
      color: 'gray',
    },
    [YearEndModuleStatusOptions.overdue]: { text: 'Overdue', color: 'red' },
    [YearEndModuleStatusOptions.missed]: { text: 'Missed', color: 'red' },
  }

const getDescription = (
  status: YearEndModuleStatusOptions,
  substep: string | null,
  isBusiness: boolean,
  isTwoFormFiler: boolean,
  taxYear: string
) => {
  switch (status) {
    case YearEndModuleStatusOptions.upcoming:
      if (isBusiness) {
        return `We will send your ${taxYear} business return to a tax preparer once your books are finalized.`
      }
      return isTwoFormFiler
        ? 'After finishing your business tax return, your tax preparer will help you finalize and file your personal tax return.'
        : 'We’ll connect you with a tax preparer from our partner TaxFyle. They’ll work with you to finalize and submit your return.'
    case YearEndModuleStatusOptions.waitingOnHeard:
      switch (substep) {
        case FormFilingJobSubstep.jobStarted:
          return 'Your return has been started.'
        case FormFilingJobSubstep.notClaimed:
          return `We’re matching you to a tax preparer who will handle your ${isBusiness ? 'business tax return' : 'tax return'}. Your bookkeeping and tax information will be sent to them.`
        case FormFilingJobSubstep.inProgress:
          return `Your tax preparer is currently working on your ${isBusiness ? 'business tax return' : 'tax return'}. They’ll reach out if they have any questions.`
        case FormFilingJobSubstep.draftApproved:
          return 'Your tax preparer will now send you a form to sign to give them permission to e-file on your behalf.'
        case FormFilingJobSubstep.signedAndApproved:
          return `We’re filing your ${isBusiness ? 'Form 1120-S' : 'Form 1040'} with the IRS.`
        default:
          return 'Your tax preparer is working on your return.'
      }
    case YearEndModuleStatusOptions.actionRequired:
      switch (substep) {
        case FormFilingJobSubstep.startedUserCTA:
          return 'Your return has been started.'
        case FormFilingJobSubstep.inProgressNeedsUserAction:
          return 'Your tax preparer has questions about your information.'
        case FormFilingJobSubstep.draftReadyForReview:
          return 'Your tax return draft is ready for review.'
        case FormFilingJobSubstep.signatureRequested:
          return 'Your tax preparer has sent you a form to sign to give them permission to e-file your return on your behalf.'
        case FormFilingJobSubstep.updatePaymentInfo:
          return `Your ${isBusiness ? 'Form 1120-S' : 'Form 1040'} has been successfully filed with the IRS. Please update Heard with the amount of your tax payment or refund.`
        case FormFilingJobSubstep.rejectedByIRS:
          return 'Unfortunately, your tax return was rejected by the IRS. Please work with your tax preparer to resolve any issues with your return.'
        default:
          return 'Your tax return is being processed.'
      }
    case YearEndModuleStatusOptions.complete:
      return 'Your tax return has been filed.'
    case YearEndModuleStatusOptions.cancelled:
      return 'Your tax filing has been cancelled. No further action needed.'
    default:
      return `Your ${taxYear} ${isBusiness ? 'business' : 'personal'} return is with your tax preparer.`
  }
}

export const PostTaxYearFilingCard = ({
  form,
  taxYear,
  isTwoFormFiler,
  onUpdated,
}: {
  form: PostTaxYearForm
  taxYear: string
  isTwoFormFiler: boolean
  onUpdated?: () => void
}) => {
  const dispatch = useAppDispatch()
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  // Payment and refund amounts are recorded against the filing form, so this
  // reaches a prior year without going through the open season's selectors.
  const wantsPaymentInfo =
    form.substepIdentifier === FormFilingJobSubstep.updatePaymentInfo
  const outcomes = useReselector(
    selectAnnualTaxOutcomesByFormId,
    form.filingFormId
  )

  const [outcomesLoaded, setOutcomesLoaded] = useState(false)

  useEffect(() => {
    if (!wantsPaymentInfo) {
      return undefined
    }
    let active = true
    setOutcomesLoaded(false)
    // Forced: the fetch key is shared across forms, so "if needed" treats any
    // earlier outcomes fetch as this one and leaves the store on another form.
    void dispatch(fetchAnnualTaxOutcomesIfNeeded(form.filingFormId, true)).then(
      () => {
        if (active) {
          setOutcomesLoaded(true)
        }
      }
    )
    return () => {
      active = false
    }
  }, [dispatch, form.filingFormId, wantsPaymentInfo])
  const isBusiness = form.formType === FORM_1120_S
  const status = form.moduleStatus ?? YearEndModuleStatusOptions.upcoming
  const label: StatusLabel = STATUS_LABELS[status] ?? {
    text: 'In Progress',
    color: 'orange',
  }
  // The year end cards' rule: never while the job is unclaimed, and only on the
  // statuses where there is something to say.
  const isClaimed = form.substepIdentifier !== FormFilingJobSubstep.notClaimed
  const showButton =
    isClaimed &&
    (wantsPaymentInfo || form.withPreparer) &&
    BUTTON_STATUSES.includes(status)
  const buttonText = wantsPaymentInfo
    ? 'Update Payment or Refund'
    : getDefaultButtonText(status)
  // A finished 1040 keeps the updatePaymentInfo substep, so this keys on status.
  const showChatLink =
    isClaimed &&
    form.withPreparer &&
    (status === YearEndModuleStatusOptions.complete ||
      (status === YearEndModuleStatusOptions.actionRequired &&
        wantsPaymentInfo))

  const fetchArchiveJobUrl = useCallback(
    () =>
      fetchTaxFyleJobUrl(form.filingFormId, {
        url: `${window.location.origin}/taxes/archive`,
        actionLabel: 'Tax Returns',
      }),
    [form.filingFormId]
  )
  const {
    open: openPortal,
    loading: portalLoading,
    error: portalError,
  } = useOpenTaxfylePortal(
    fetchArchiveJobUrl,
    FETCH_TAXFYLE_FORM_JOB_URL_KEY(form.filingFormId)
  )

  const onActionClick = useCallback(() => {
    if (wantsPaymentInfo) {
      setPaymentModalOpen(true)
      return
    }
    void openPortal()
  }, [openPortal, wantsPaymentInfo])

  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col items-start gap-3">
          <Pill color={label.color} size="sm" uppercase>
            {label.text}
          </Pill>
          <div className="flex flex-col gap-1">
            <Text variant={TypographyStyles.DISPLAY_XXS}>
              {isBusiness
                ? 'File Business Tax Return'
                : 'File Personal Tax Return'}
            </Text>
            {status === YearEndModuleStatusOptions.upcoming && (
              <Text
                variant={TypographyStyles.BODY_XS}
                color={TextColors.MUTED}
                className="flex items-center"
              >
                <Icon
                  icon={faTimer}
                  className="mr-1"
                  color={TextColors.MUTED}
                />
                7-10 days
              </Text>
            )}
          </div>
          <Text variant={TypographyStyles.BODY_SM}>
            {getDescription(
              status,
              form.substepIdentifier,
              isBusiness,
              isTwoFormFiler,
              taxYear
            )}
          </Text>
          {showButton && (
            <Button
              variant="secondary"
              loading={wantsPaymentInfo ? !outcomesLoaded : portalLoading}
              onClick={onActionClick}
            >
              {buttonText}
            </Button>
          )}
          {showChatLink && (
            <Button
              variant="link"
              loading={portalLoading}
              onClick={() => {
                void openPortal()
              }}
            >
              View Chat
            </Button>
          )}
          {portalError && (
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.DANGER}>
              {portalError}
            </Text>
          )}
        </div>
        {/* The modal seeds its form from outcomes on mount and never
            reinitialises, so it opens only once this form's outcomes are in. */}
        {wantsPaymentInfo && paymentModalOpen && (
          <AnnualTax1040WrapUpModal
            modalOpen={paymentModalOpen}
            onClose={() => {
              setPaymentModalOpen(false)
              onUpdated?.()
            }}
            year={taxYear}
            outcomes={outcomes.filter(
              (outcome) => outcome.isForExtension !== true
            )}
          />
        )}
        <Image
          src={
            isBusiness
              ? 'https://heard-images.s3.amazonaws.com/assets/1120s-form.svg'
              : 'https://heard-images.s3.amazonaws.com/assets/1040-form.svg'
          }
          alt={isBusiness ? '1120-S form' : '1040 form'}
          width={80}
          height={80}
        />
      </div>
    </Card>
  )
}

export default PostTaxYearFilingCard
